import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import noop from 'lodash/noop';

import { connectToSubscription, getSubscription } from '../api/subscriptions.api';
import {
  calculateSubscriptionProvisioningStatus,
  getConnectionString,
  shouldSkipProvisioning,
} from '../utils/subscription.utils';
import { findDefaultUrl } from '../utils/subscription.utils';

import { getUsePollConnectFlow } from './usePollConnectFlow';

import type {
  ConnectSubscriptionStatus,
  ConnectToSubscriptionResults,
  Subscription,
} from '../types/subscription.types';
import type { RootState } from 'src/types/state.types';

const MAX_RETRIES = 15;

const usePollProvisioningConnectFlow = getUsePollConnectFlow([
  'provisioning',
  'no_default_connection',
]);
const usePollHibernatedConnectFlow = getUsePollConnectFlow(['hibernated']);
const usePollToConnectFlow = getUsePollConnectFlow(
  ['poll_until_connect', 'network_problem', 'trying_to_connect'],
  MAX_RETRIES
);

export const useConnectSubscriptionFlow = (
  lastUrl: string,
  subscriptionId: string,
  connected: boolean
) => {
  const [nodeStatus, setNodeStatus] = useState<{
    nodeStatus?: ConnectSubscriptionStatus;
    connectSubscriptionStatus?: ConnectToSubscriptionResults;
    url?: string;
  } | null>();
  const sub = useRef<Subscription>(undefined);
  const customConnectionURLs = useSelector((state: RootState) => state.customConnectionURLs);

  const getSub = () =>
    getSubscription(lastUrl, subscriptionId)
      .then((subscription) => {
        if (subscription) {
          sub.current = subscription;
          const defaultUrl = findDefaultUrl(subscription);

          if (shouldSkipProvisioning(subscription)) {
            if (!defaultUrl) {
              return setNodeStatus({ nodeStatus: 'no_default_connection' });
            }

            return setNodeStatus({ nodeStatus: 'trying_to_connect' });
          }

          const status = calculateSubscriptionProvisioningStatus(
            subscription.provisioning_status ?? 'completed',
            subscription.was_hibernated_due_to_idleness ?? false
          );

          // TODO: add message for completed node without default connection string. In that case it should use 'trying_to_connect' and not 'poll_until_connect'

          setNodeStatus({ nodeStatus: status });
        } else {
          console.error('Subscription not found or could not be fetched');
        }
      })
      .catch((error) => {
        console.error('Error fetching subscription:', error);
      });

  const connectSubscription = async () => {
    const persistedURL = getConnectionString(sub.current?.id || '');
    const apiUrl = persistedURL ?? findDefaultUrl(sub.current) ?? '';
    const customConnectionURL = customConnectionURLs[sub.current?.id || ''];

    const finalUrl = persistedURL
      ? persistedURL
      : customConnectionURL
      ? customConnectionURL
      : apiUrl;

    try {
      const connectToSubscriptionResults = await connectToSubscription({
        portalUrl: lastUrl,
        subscriptionId,
        apiUrl: finalUrl,
      });

      if (connectToSubscriptionResults) {
        setNodeStatus({
          nodeStatus: connectToSubscriptionResults.preStatus,
          connectSubscriptionStatus: connectToSubscriptionResults,
          url: apiUrl,
        });
      } else {
        console.error('Subscription not found or could not be fetched');
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  usePollProvisioningConnectFlow({
    currentNodeStatus: nodeStatus?.nodeStatus,
    callbackFn: connected ? noop : getSub,
  });

  usePollHibernatedConnectFlow({
    currentNodeStatus: nodeStatus?.nodeStatus,
    callbackFn: connected ? noop : getSub,
  });

  usePollToConnectFlow({
    currentNodeStatus: nodeStatus?.nodeStatus,
    callbackFn: connected ? noop : connectSubscription,
  });

  useEffect(() => {
    if (!nodeStatus) {
      getSub();
    }
  }, []);

  return nodeStatus;
};
